import cancelSend from './cancelSend';
import closeCompose from './closeCompose';
import { messageBeingSent } from './messageBeingSent';
import onInlineComposeSendStateChange from './onInlineComposeSendStateChange';
import removeInfoBarMessage from './removeInfoBarMessage';
import setAsyncSendState from './setAsyncSendState';
import setIsSending from './setIsSending';
import setPreferAsyncSend from './setPreferAsyncSend';
import trySaveMessage, { isSaving, waitForActiveSaving } from './trySaveMessage';
import datapoints from '../datapoints';
import { findComposePlugins } from '../plugins/ComposePlugins';
import ComposeViewState, { AsyncSendState } from '../store/schema/ComposeViewState';
import { getAllRecipients } from '../utils/getAllRecipientsAsEmailAddressStrings';
import getFriendlySubject from '../utils/getFriendlySubject';
import getMailTipsViewStateMap from '../utils/getMailTipsViewStateMap';
import { handleSendErrorResponse } from '../utils/handleSendErrorResponse';
import { sendAndSaveInfobarIdsToRemove } from '../utils/InfoBarMessageId';
import { logAttachmentDatapoints } from '../utils/logAttachmentDatapoints';
import logSendActionMailTipsDatapoint from '../utils/logSendActionMailTipsDatapoint';
import { ComposeScenario, logSmartReplyDatapoint } from '../utils/logSmartReplyComposeDatapoint';
import { reopenFailedMessage } from '../utils/reopenFailedMessage';
import safelyGetSmartReplyExtractionId from '../utils/safelyGetSmartReplyExtractionId';
import { UNDO_SEND_ERROR_MESSAGE } from '../utils/sendConstants';
import { CustomError, handleSuccessResponse, sendMessage } from '../utils/sendSaveUtils';
import shouldUseAsyncSend from '../utils/shouldUseAsyncSend';
import validateSend from '../utils/validateSend';
import endSession from 'owa-controls-findpeople-feedback-manager/lib/actions/endSession';
import addMilliseconds from 'owa-datetime/lib/owaDate/addMilliseconds';
import now from 'owa-datetime/lib/userDate/now';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isGroupScenario, isViewStateDirty } from 'owa-mail-compose-store/lib/utils/viewStateUtils';
import { getDelayedItemStore, lazyRemoveMailItemWithData } from 'owa-mail-delaysend-store';
import showNotificationOnSend from 'owa-mail-delaysend-store/lib/utils/showNotificationOnSend';
import { lazyClosePreview } from 'owa-preview-pane-store';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import strings from 'owa-strings/owa-mail-strings';
import { isSxSDisplayed } from 'owa-sxs-store';
import { action } from 'satcheljs/lib/legacy';
import {
    DatapointStatus,
    PerformanceDatapoint,
    logUsage,
    returnTopExecutingActionDatapoint,
    ActionDatapoint,
} from 'owa-analytics';
import {
    lazyAddNotification,
    ensureNotificationHandlerRegistered,
    NotificationActionType,
    NotificationViewState,
} from 'owa-header-toast-notification';

function asyncSend(
    viewState: ComposeViewState,
    startSendDatapoint: () => void,
    e2eDatapoint: ActionDatapoint
): Promise<ItemInfoResponseMessage> {
    const delaySendIntervalSeconds = getUserConfiguration().UserOptions.MailSendUndoInterval
        ? getUserConfiguration().UserOptions.MailSendUndoInterval
        : 0;

    // Start to send here before other code to avoid any client side error interrupt sending
    const sendMessagePromise: Promise<ItemInfoResponseMessage> =
        isFeatureEnabled('cmp-delaySend') && delaySendIntervalSeconds > 0 && !isSxSDisplayed()
            ? handleDelayedSendMessage(
                  viewState,
                  startSendDatapoint,
                  delaySendIntervalSeconds * 1000
              )
            : handleSendMessage(viewState, startSendDatapoint);

    setIsSending(viewState, true /*isSending*/);
    if (!viewState.isInlineCompose) {
        lazyClosePreview.importAndExecute('sendMessage');
    }

    e2eDatapoint.markUserPerceivedTime(true);

    return sendMessagePromise;
}

async function syncSend(
    viewState: ComposeViewState,
    startSendDatapoint: () => void
): Promise<ItemInfoResponseMessage> {
    startSendDatapoint();
    setIsSending(viewState, true /*isSending*/);

    const responseMessage = await sendMessage(viewState);
    endSession(viewState.findPeopleFeedbackManager, 'Send');
    closeCompose(viewState);

    if (viewState.isInlineCompose) {
        onInlineComposeSendStateChange(viewState, true /*isSendPending*/);
    }

    return Promise.resolve(responseMessage);
}

function handleSendMessage(
    viewState: ComposeViewState,
    startSendDatapoint: () => void
): Promise<ItemInfoResponseMessage> {
    startSendDatapoint();
    logAttachmentDatapoints(viewState);

    setAsyncSendState(viewState, AsyncSendState.Sending);
    if (viewState.isInlineCompose) {
        onInlineComposeSendStateChange(viewState, true /*isSendPending*/);
    }
    // Set to preferAsyncSend to false in case the send failed, then the retry will use sync send
    setPreferAsyncSend(viewState, false /* preferAsyncSend */);

    return sendMessage(viewState)
        .then(responseMessage => {
            // Handle the response here to avoid false alerm of sending failure caused by client side error
            setAsyncSendState(viewState, AsyncSendState.Sent);
            return Promise.resolve(responseMessage);
        })
        .catch((error: CustomError) => {
            // Only show the send failure notification when we really got server side sending error
            handleFailedOrCanceledAsyncSend(viewState);
            error.showErrorToUser = true;
            return Promise.reject(error);
        });
}

function handleDelayedSendMessage(
    viewState: ComposeViewState,
    startSendDatapoint: () => void,
    delaySendIntervalMilliseconds: number
): Promise<ItemInfoResponseMessage> {
    setAsyncSendState(viewState, AsyncSendState.Delay);
    if (viewState.isInlineCompose) {
        onInlineComposeSendStateChange(viewState, true /*isSendPending*/);
    }

    return new Promise<ItemInfoResponseMessage>((resolve, reject) => {
        // Start delay timer and show Undo send notification bar
        const delaySendTimer = window.setTimeout(async function() {
            lazyRemoveMailItemWithData.importAndExecute(viewState);
            try {
                resolve(await handleSendMessage(viewState, startSendDatapoint));
            } catch (error) {
                reject(error);
            }
        }, delaySendIntervalMilliseconds);
        showNotificationOnSend(delaySendIntervalMilliseconds);

        // Update the delayedSendStore and prep for cancel send
        getDelayedItemStore().delayedMailItems.push({
            timeoutId: delaySendTimer,
            timeToSend: addMilliseconds(now(), delaySendIntervalMilliseconds),
            data: viewState,
            afterCancel: data => {
                cancelSend(data);
                handleFailedOrCanceledAsyncSend(viewState);
                reject(new Error(UNDO_SEND_ERROR_MESSAGE));
            },
        });

        // Save draft
        if (!isGroupScenario(viewState) && !isSaving(viewState) && isViewStateDirty(viewState)) {
            trySaveMessage(viewState, true /*isAutoSave*/);
        }
    });
}

function onAsyncSendNotificationClick(notification: NotificationViewState) {
    const viewState = notification.actionContext as ComposeViewState;
    reopenFailedMessage(viewState.composeId);
}

function addErrorNotification(viewState: ComposeViewState) {
    const subject = getFriendlySubject(viewState.subject);

    ensureNotificationHandlerRegistered(
        NotificationActionType.AsyncSendFailed,
        onAsyncSendNotificationClick
    );

    const notification = {
        actionType: NotificationActionType.AsyncSendFailed,
        title: strings.failToSendMessageError,
        description: subject,
        actionLink: strings.moreDetailsNotificationLabel,
        cancelLink: null,
        actionContext: viewState,
    };

    lazyAddNotification.importAndExecute(notification as NotificationViewState);
}

function handleFailedOrCanceledAsyncSend(viewState: ComposeViewState) {
    if (viewState.isInlineCompose) {
        onInlineComposeSendStateChange(viewState, false /*isSendPending*/);
    }

    // Only do this in Sending state because Sending means the viewState is still pending,
    // while it will be Timeout state after a period, in that case the viewState is floating rather than pending/
    // We don't need to show error notification for floating compose because the form itself is already visible
    if (viewState.asyncSendState == AsyncSendState.Sending) {
        addErrorNotification(viewState);
    }

    if (viewState.asyncSendState != AsyncSendState.None) {
        setAsyncSendState(viewState, AsyncSendState.Error);
    }

    setIsSending(viewState, false /*isSending*/);
}

function endCtq(
    datapoint: PerformanceDatapoint,
    smartReplyExtractionId: string,
    viewState: ComposeViewState,
    error?: CustomError
) {
    if (datapoint) {
        window.requestAnimationFrame(() => {
            // Add extraction ID for smart reply, so we can generate meaningful experiment data.
            datapoint.addCosmosOnlyData(
                JSON.stringify({ smartReplyExtractionId: smartReplyExtractionId })
            );

            if (error) {
                datapoint.addCustomData(
                    datapoint.eventName == datapoints.MailComposeSend.datapoint.name
                        ? datapoints.MailComposeSend.datapoint.customData(
                              error.showErrorToUser,
                              error.isAsyncSend,
                              error.messageText
                          )
                        : datapoints.GroupMailComposeSend.datapoint.customData(
                              viewState,
                              error.showErrorToUser,
                              error.isAsyncSend
                          )
                );
                datapoint.endWithError(DatapointStatus.ServerError, error);
            } else {
                datapoint.end();
            }
        });
    }
}

export default action('trySendMessage', datapoints.MailComposeSendE2E)(
    async function trySendMessage(
        viewState: ComposeViewState,
        bypassClientSideValidation?: boolean
    ) {
        if (viewState.isSending) {
            // Avoid duplicated sending
            return Promise.resolve();
        }
        const mailComposeSendE2EDataPoint = returnTopExecutingActionDatapoint();
        const useAsyncSend = shouldUseAsyncSend(viewState);

        removeInfoBarMessage(viewState, sendAndSaveInfobarIdsToRemove);

        const errorCode =
            !bypassClientSideValidation &&
            (await validateSend(viewState, mailComposeSendE2EDataPoint));

        if (!errorCode) {
            await waitForActiveSaving(viewState);

            messageBeingSent();

            // Track the real sending failure, client side validation failure is excluded
            let sendDatapoint: PerformanceDatapoint = null;

            const startSendDatapoint = () => {
                if (isGroupScenario(viewState)) {
                    sendDatapoint = new PerformanceDatapoint(
                        datapoints.GroupMailComposeSend.datapoint.name,
                        datapoints.GroupMailComposeSend.datapoint.options
                    );
                } else {
                    sendDatapoint = new PerformanceDatapoint(
                        datapoints.MailComposeSend.datapoint.name,
                        datapoints.MailComposeSend.datapoint.options
                    );
                }
            };

            const sendFunction = useAsyncSend ? asyncSend : syncSend;

            let responseMessage: ItemInfoResponseMessage = null;
            try {
                responseMessage = await sendFunction(
                    viewState,
                    startSendDatapoint,
                    mailComposeSendE2EDataPoint
                );

                if (isGroupScenario(viewState)) {
                    sendDatapoint.addCustomData(
                        datapoints.GroupMailComposeSend.datapoint.customData(viewState, null, null)
                    );
                }
            } catch (error) {
                error.isAsyncSend = useAsyncSend;
                setIsSending(viewState, false /*isSending*/);
                endCtq(sendDatapoint, safelyGetSmartReplyExtractionId(viewState), viewState, error);
                handleSendErrorResponse(error, viewState, true /*isSend*/);
                return Promise.reject(error);
            }

            // contentOperationPlugin will do auto save when dispose. Since we have already sent the message, we need to stop this action
            const plugins = findComposePlugins(viewState);
            if (plugins && plugins.contentOperationPlugin) {
                plugins.contentOperationPlugin.stopSaveOnDispose();
            }

            // Log datapoints based on properties of message.
            if (viewState.IRMData) {
                logUsage(datapoints.MailComposeSendWithIRMData.datapoint.name);
            }
            if (viewState.isReadReceiptRequested) {
                logUsage(datapoints.MailComposeSendRequestReadReceipt.datapoint.name);
            }
            if (viewState.isDeliveryReceiptRequested) {
                logUsage(datapoints.MailComposeSendRequestDeliveryReceipt.datapoint.name);
            }
            if (viewState.sensitivity) {
                logUsage(datapoints.MailComposeSendWithMessageSensitivity.datapoint.name);
            }

            // If we are sending a message created via smart reply, log a datapoint.
            logSmartReplyDatapoint(
                ComposeScenario.Send,
                safelyGetSmartReplyExtractionId(viewState)
            );

            logSendActionMailTipsDatapoint(
                getMailTipsViewStateMap(viewState.ccRecipientWell, getAllRecipients(viewState))
            );
            setIsSending(viewState, false /*isSending*/);
            endCtq(sendDatapoint, safelyGetSmartReplyExtractionId(viewState), viewState);
            const handledSuccessfully = handleSuccessResponse(responseMessage, viewState);
            if (handledSuccessfully) {
                return Promise.resolve();
            } else {
                mailComposeSendE2EDataPoint.endWithError(
                    DatapointStatus.ServerError,
                    new Error('Malformed response shape')
                );
                // Returning as a regular resolve here, as I'm not sure of the side effects.
                return Promise.resolve();
            }
        } else {
            setIsSending(viewState, false /*isSending*/);
            mailComposeSendE2EDataPoint.endWithError(
                DatapointStatus.UserError,
                new Error(errorCode)
            );
            return Promise.reject(new Error(errorCode));
        }
    }
);
